// src/components/cookies_disclaimer/cookies_disclaimer.js
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import "./cookies_disclaimer.css";

// Import translations
import enTranslations from "./en.json";
import deTranslations from "./de.json";

const CookiesDisclaimer = () => {
  const { t, i18n } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [cookieSettings, setCookieSettings] = useState({
    functional: true, // Always enabled
    statistics: false,
    marketing: false,
  });

  // Add translations for this component
  i18n.addResourceBundle("en", "cookies", enTranslations, true, true);
  i18n.addResourceBundle("de", "cookies", deTranslations, true, true);

  useEffect(() => {
    const cookiesAccepted = localStorage.getItem("cookiesAccepted");
    if (!cookiesAccepted) {
      setIsVisible(true);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem("cookiesAccepted", "true");
    localStorage.setItem(
      "cookieSettings",
      JSON.stringify({
        functional: true,
        statistics: true,
        marketing: true,
      })
    );
    setIsVisible(false);
  };

  const handleDecline = () => {
    localStorage.setItem("cookiesAccepted", "false");
    localStorage.setItem(
      "cookieSettings",
      JSON.stringify({
        functional: true,
        statistics: false,
        marketing: false,
      })
    );
    setIsVisible(false);
  };

  const handleShowSettings = () => {
    setShowSettings(true);
  };

  const handleBackToMain = () => {
    setShowSettings(false);
  };

  const handleSaveSettings = () => {
    const hasAccepted = cookieSettings.statistics || cookieSettings.marketing;
    localStorage.setItem("cookiesAccepted", hasAccepted ? "true" : "false");
    localStorage.setItem("cookieSettings", JSON.stringify(cookieSettings));
    setIsVisible(false);
  };

  const handleToggleSetting = (category) => {
    if (category === "functional") return; // Functional cookies cannot be disabled
    setCookieSettings((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const handleToggleExpand = (category) => {
    setExpandedCategory(expandedCategory === category ? null : category);
  };

  const handleKeyDown = (e, action) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      action();
    }
  };

  const handleExpandKeyDown = (e, category) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleToggleExpand(category);
    }
  };

  if (!isVisible) return null;

  return (
    <div
      className="cookies-banner"
      role="dialog"
      aria-labelledby="cookie-title"
      aria-describedby="cookie-message"
    >
      <div className="cookies-message">
        {!showSettings ? (
          <>
            <p id="cookie-message" tabIndex="0" role="text">
              {t("cookies:message")}
            </p>
            <a
              className="link-text"
              href={i18n.language === "en" ? "/en/disclaimer" : "/disclaimer"}
              aria-label={
                t("cookies:disclaimer_link") + " - öffnet in neuem Tab"
              }
              tabIndex="0"
            >
              {t("cookies:disclaimer_link")}
            </a>
            <a
              className="link-text"
              href={
                i18n.language === "en"
                  ? "/en/privacy_notice"
                  : "/privacy_notice"
              }
              aria-label={t("cookies:privacy_link") + " - öffnet in neuem Tab"}
              tabIndex="0"
            >
              {t("cookies:privacy_link")}
            </a>
            <div
              className="cookies-buttons"
              role="group"
              aria-label="Cookie-Entscheidungen"
            >
              <button
                onClick={handleAccept}
                onKeyDown={(e) => handleKeyDown(e, handleAccept)}
                aria-label="Alle Cookies akzeptieren"
                tabIndex="0"
              >
                {t("cookies:accept")}
              </button>
              <button
                onClick={handleDecline}
                onKeyDown={(e) => handleKeyDown(e, handleDecline)}
                aria-label="Nur notwendige Cookies akzeptieren"
                tabIndex="0"
              >
                {t("cookies:decline")}
              </button>
              <button
                onClick={handleShowSettings}
                onKeyDown={(e) => handleKeyDown(e, handleShowSettings)}
                aria-label="Cookie-Einstellungen anpassen"
                tabIndex="0"
              >
                {t("cookies:settings")}
              </button>
            </div>
          </>
        ) : (
          <>
            <h3 id="cookie-title" tabIndex="0">
              Cookie Settings
            </h3>
            <div
              className="cookie-settings"
              role="group"
              aria-labelledby="cookie-title"
            >
              <div
                className="cookie-category"
                role="group"
                aria-label="Funktionale Cookies"
              >
                <div
                  className="cookie-header"
                  onClick={() => handleToggleExpand("functional")}
                  onKeyDown={(e) => handleExpandKeyDown(e, "functional")}
                  tabIndex="0"
                  role="button"
                  aria-label="Funktionale Cookies Informationen ein-/ausklappen"
                >
                  <label
                    className="cookie-toggle"
                    onClick={(e) => {
                      e.preventDefault();
                      handleToggleExpand("functional");
                    }}
                    tabIndex="-1"
                    role="presentation"
                  >
                    <input
                      type="checkbox"
                      checked={cookieSettings.functional}
                      disabled
                      onClick={(e) => e.stopPropagation()}
                      aria-label="Funktionale Cookies - immer aktiviert"
                      tabIndex="-1"
                    />
                    <span className="toggle-text" role="text">
                      {t("cookies:functional")} <small>(Required)</small>
                    </span>
                  </label>
                  <button
                    className="expand-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleExpand("functional");
                    }}
                    onKeyDown={(e) => handleExpandKeyDown(e, "functional")}
                    aria-label={
                      expandedCategory === "functional"
                        ? "Details ausblenden"
                        : "Details anzeigen"
                    }
                    aria-expanded={expandedCategory === "functional"}
                    tabIndex="0"
                  >
                    {expandedCategory === "functional" ? "−" : "+"}
                  </button>
                </div>
                <div
                  className={`cookie-description ${
                    expandedCategory === "functional" ? "expanded" : ""
                  }`}
                  role="region"
                  aria-label="Beschreibung funktionale Cookies"
                  tabIndex={expandedCategory === "functional" ? "0" : "-1"}
                >
                  {t("cookies:functional_description")}
                </div>
              </div>
              <div
                className="cookie-category"
                role="group"
                aria-label="Statistik-Cookies"
              >
                <div
                  className="cookie-header"
                  onClick={() => handleToggleExpand("statistics")}
                  onKeyDown={(e) => handleExpandKeyDown(e, "statistics")}
                  tabIndex="0"
                  role="button"
                  aria-label="Statistik-Cookies Informationen ein-/ausklappen"
                >
                  <label
                    className="cookie-toggle"
                    onClick={(e) => {
                      e.preventDefault();
                      handleToggleExpand("statistics");
                    }}
                    tabIndex="-1"
                    role="presentation"
                  >
                    <input
                      type="checkbox"
                      checked={cookieSettings.statistics}
                      onChange={() => handleToggleSetting("statistics")}
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          handleToggleSetting("statistics");
                        }
                      }}
                      aria-label="Statistik-Cookies aktivieren oder deaktivieren"
                      tabIndex="0"
                    />
                    <span className="toggle-text" role="text">
                      {t("cookies:statistics")}
                    </span>
                  </label>
                  <button
                    className="expand-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleExpand("statistics");
                    }}
                    onKeyDown={(e) => handleExpandKeyDown(e, "statistics")}
                    aria-label={
                      expandedCategory === "statistics"
                        ? "Details ausblenden"
                        : "Details anzeigen"
                    }
                    aria-expanded={expandedCategory === "statistics"}
                    tabIndex="0"
                  >
                    {expandedCategory === "statistics" ? "−" : "+"}
                  </button>
                </div>
                <div
                  className={`cookie-description ${
                    expandedCategory === "statistics" ? "expanded" : ""
                  }`}
                  role="region"
                  aria-label="Beschreibung Statistik-Cookies"
                  tabIndex={expandedCategory === "statistics" ? "0" : "-1"}
                >
                  {t("cookies:statistics_description")}
                </div>
              </div>
              <div
                className="cookie-category"
                role="group"
                aria-label="Marketing-Cookies"
              >
                <div
                  className="cookie-header"
                  onClick={() => handleToggleExpand("marketing")}
                  onKeyDown={(e) => handleExpandKeyDown(e, "marketing")}
                  tabIndex="0"
                  role="button"
                  aria-label="Marketing-Cookies Informationen ein-/ausklappen"
                >
                  <label
                    className="cookie-toggle"
                    onClick={(e) => {
                      e.preventDefault();
                      handleToggleExpand("marketing");
                    }}
                    tabIndex="-1"
                    role="presentation"
                  >
                    <input
                      type="checkbox"
                      checked={cookieSettings.marketing}
                      onChange={() => handleToggleSetting("marketing")}
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          handleToggleSetting("marketing");
                        }
                      }}
                      aria-label="Marketing-Cookies aktivieren oder deaktivieren"
                      tabIndex="0"
                    />
                    <span className="toggle-text" role="text">
                      {t("cookies:marketing")}
                    </span>
                  </label>
                  <button
                    className="expand-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleExpand("marketing");
                    }}
                    onKeyDown={(e) => handleExpandKeyDown(e, "marketing")}
                    aria-label={
                      expandedCategory === "marketing"
                        ? "Details ausblenden"
                        : "Details anzeigen"
                    }
                    aria-expanded={expandedCategory === "marketing"}
                    tabIndex="0"
                  >
                    {expandedCategory === "marketing" ? "−" : "+"}
                  </button>
                </div>
                <div
                  className={`cookie-description ${
                    expandedCategory === "marketing" ? "expanded" : ""
                  }`}
                  role="region"
                  aria-label="Beschreibung Marketing-Cookies"
                  tabIndex={expandedCategory === "marketing" ? "0" : "-1"}
                >
                  {t("cookies:marketing_description")}
                </div>
              </div>
            </div>
            <div
              className="cookies-buttons"
              role="group"
              aria-label="Cookie-Einstellungen speichern"
            >
              <button
                onClick={handleBackToMain}
                onKeyDown={(e) => handleKeyDown(e, handleBackToMain)}
                aria-label="Zurück zur Hauptansicht"
                tabIndex="0"
              >
                {t("cookies:back")}
              </button>
              <button
                onClick={handleSaveSettings}
                onKeyDown={(e) => handleKeyDown(e, handleSaveSettings)}
                aria-label="Cookie-Einstellungen speichern und schließen"
                tabIndex="0"
              >
                {t("cookies:save_settings")}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CookiesDisclaimer;
